import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertTriangle, Plus, RotateCcw, Trash2, Check, Loader2 } from "lucide-react";

function eur(cents) { return `€${((cents || 0) / 100).toFixed(2)}`; }

export function RefundPolicyTab() {
  const t = useT();
  const [tiers, setTiers] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [samplePrice, setSamplePrice] = useState("100");
  const [sampleTier, setSampleTier] = useState(0);
  const [estimate, setEstimate] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/coach/refund-policy");
      setTiers(data?.tiers || []);
      setDefaults(data?.platform_default || []);
      setNotes(data?.notes || "");
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const price = Math.max(0, Math.round(Number(samplePrice || "0") * 100));
    const tier = tiers[sampleTier];
    if (!price || !tier) { setEstimate(null); return; }
    let cancelled = false;
    const id = setTimeout(async () => {
      try {
        const { data } = await api.get(`/coach/refund-cost-estimate?price_cents=${price}&refund_pct=${tier.refund_pct}`);
        if (!cancelled) setEstimate(data);
      } catch { if (!cancelled) setEstimate(null); }
    }, 300);
    return () => { cancelled = true; clearTimeout(id); };
  }, [tiers, sampleTier, samplePrice]);

  const updateTier = (idx, field, val) => setTiers((prev) => prev.map((tr, i) => i === idx ? {
    ...tr, [field]: field === "hours_before" ? Math.max(0, Math.round(Number(val || "0"))) : Math.max(0, Math.min(100, Number(val || "0"))),
  } : tr));
  const addTier = () => setTiers((prev) => [{ hours_before: (prev[0]?.hours_before ?? 72) + 24, refund_pct: 100 }, ...prev]);
  const removeTier = (idx) => setTiers((prev) => prev.filter((_, i) => i !== idx));
  const reset = () => setTiers(defaults.map((d) => ({ ...d })));

  const save = async () => {
    setSaving(true);
    try { await api.put("/coach/refund-policy", { tiers, notes }); toast.success(t("bk.rf_saved")); await load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-20 rounded-2xl" />)}</div>;

  const Row = ({ lbl, val, tone, strong }) => {
    const c = tone === "warning" ? "text-amber-600" : tone === "error" ? "text-rose-600" : tone === "success" ? "text-emerald-600" : "text-foreground";
    return (
      <div className="flex items-center justify-between py-0.5">
        <span className="text-xs text-muted-foreground">{lbl}</span>
        <span className={`text-sm ${c} ${strong ? "font-display font-semibold" : "font-medium"}`}>{val}</span>
      </div>
    );
  };

  return (
    <div data-testid="bk-refund-tab">
      <div className="flex gap-2.5 rounded-2xl border border-amber-300 bg-amber-50 p-4">
        <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" />
        <p className="text-sm text-foreground"><span className="font-semibold">{t("bk.rf_warn_bold")}</span> {t("bk.rf_warn_body")}</p>
      </div>

      <h3 className="mb-0.5 mt-6 font-display text-lg text-foreground">{t("bk.rf_your_tiers")}</h3>
      <p className="mb-3 text-xs text-muted-foreground">{t("bk.rf_tiers_hint")}</p>

      <div className="space-y-2">
        {tiers.map((tier, idx) => (
          <div key={idx} className="flex items-end gap-2" data-testid={`bk-tier-${idx}`}>
            <div className="flex-1">
              <label className="mb-1 block text-[11px] text-muted-foreground">{t("bk.rf_hours_before")}</label>
              <Input type="number" min="0" value={tier.hours_before} onChange={(e) => updateTier(idx, "hours_before", e.target.value)} className="h-10" data-testid={`bk-tier-hours-${idx}`} />
            </div>
            <div className="flex-1">
              <label className="mb-1 block text-[11px] text-muted-foreground">{t("bk.rf_percent")}</label>
              <Input type="number" min="0" max="100" value={tier.refund_pct} onChange={(e) => updateTier(idx, "refund_pct", e.target.value)} className="h-10" data-testid={`bk-tier-pct-${idx}`} />
            </div>
            <Button size="icon" variant="outline" className="h-10 w-10 shrink-0 text-rose-500 hover:text-rose-600" onClick={() => removeTier(idx)} data-testid={`bk-tier-remove-${idx}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
      <div className="mt-2 flex gap-2">
        <Button size="sm" variant="outline" className="gap-1.5 rounded-full" onClick={addTier} data-testid="bk-tier-add"><Plus className="h-3.5 w-3.5" /> {t("bk.rf_add_tier")}</Button>
        <Button size="sm" variant="outline" className="gap-1.5 rounded-full" onClick={reset} data-testid="bk-tier-reset"><RotateCcw className="h-3.5 w-3.5" /> {t("bk.rf_reset")}</Button>
      </div>

      {/* Estimator */}
      <h3 className="mb-2 mt-6 font-display text-lg text-foreground">{t("bk.rf_estimator")}</h3>
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-32">
          <label className="mb-1 block text-[11px] text-muted-foreground">{t("bk.rf_sample_price")}</label>
          <Input type="number" min="0" value={samplePrice} onChange={(e) => setSamplePrice(e.target.value)} className="h-10" data-testid="bk-est-price" />
        </div>
        <div className="flex-1">
          <label className="mb-1 block text-[11px] text-muted-foreground">{t("bk.rf_at_tier")}</label>
          <div className="flex flex-wrap gap-1.5">
            {tiers.map((tier, idx) => (
              <button key={idx} onClick={() => setSampleTier(idx)} data-testid={`bk-est-tier-${idx}`} className={`rounded-full px-2.5 py-1.5 text-xs font-medium ${sampleTier === idx ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>
                ≥{tier.hours_before}h → {tier.refund_pct}%
              </button>
            ))}
          </div>
        </div>
      </div>
      {estimate && (
        <div className="mt-3 rounded-2xl border border-border bg-secondary/40 p-4" data-testid="bk-est-card">
          <Row lbl={t("bk.rf_refund_amount")} val={eur(estimate.refund_amount_cents)} tone="warning" />
          <Row lbl={t("bk.rf_stripe_fee")} val={eur(estimate.stripe_fee_on_charge_cents)} tone="warning" />
          <Row lbl={t("bk.rf_out_of_pocket")} val={eur(estimate.coach_out_of_pocket_cents)} tone="error" strong />
          <div className="my-2 h-px bg-border" />
          <Row lbl={t("bk.rf_commission_kept")} val={eur(estimate.platform_commission_kept_cents)} />
          <Row lbl={t("bk.rf_net_kept")} val={`${estimate.coach_net_kept_cents < 0 ? "−" : ""}${eur(Math.abs(estimate.coach_net_kept_cents))}`} tone={estimate.coach_net_kept_cents < 0 ? "error" : "success"} strong />
        </div>
      )}

      {/* Notes */}
      <h3 className="mb-2 mt-6 font-display text-lg text-foreground">{t("bk.rf_msg_to_clients")}</h3>
      <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={1000} placeholder={t("bk.rf_msg_ph")} className="min-h-[80px]" data-testid="bk-policy-notes" />

      <Button className="mt-4 w-full gap-1.5 rounded-full sm:w-auto" disabled={saving} onClick={save} data-testid="bk-refund-save">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {t("bk.rf_save")}
      </Button>
    </div>
  );
}
